import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ArrowLeft, ArrowRight } from "lucide-react";
import QuestionRenderer from "./QuestionRenderer";
import ProgressIndicator from "./ProgressIndicator";
import { assessmentQuestions, Question } from "@/data/assessmentQuestions";

interface AssessmentContentProps {
  currentQuestionIndex: number;
  progress: number;
  currentQuestion: Question;
  currentAnswer: any;
  isAnswered: boolean;
  isProcessing: boolean;
  onAnswer: (answer: string | string[]) => void;
  onPrevious: () => void;
  onNext: () => void;
}

const AssessmentContent = ({
  currentQuestionIndex,
  progress,
  currentQuestion,
  currentAnswer,
  isAnswered,
  isProcessing,
  onAnswer,
  onPrevious,
  onNext
}: AssessmentContentProps) => {
  return (
    <main className="container mx-auto px-3 py-3 md:px-6 md:py-6 max-w-4xl">
      <div className="max-w-2xl mx-auto">
        <ProgressIndicator 
          currentIndex={currentQuestionIndex}
          totalQuestions={assessmentQuestions.length}
          progress={progress}
        />

        {/* Question Card */}
        <Card className="p-3 md:p-6 bg-card/95 backdrop-blur-sm border-border/50 shadow-sm">
          {isProcessing ? (
            <div className="text-center py-8 md:py-12">
              <div className="animate-spin rounded-full h-10 w-10 md:h-12 md:w-12 border-b-2 border-primary mx-auto mb-3 md:mb-4"></div>
              <h2 className="text-base md:text-xl font-semibold text-foreground mb-2">
                Analyzing Your Responses
              </h2>
              <p className="text-sm text-muted-foreground">
                Our AI is processing your assessment with the latest medical evidence...
              </p>
            </div>
          ) : (
            <>
              <div className="mb-4 md:mb-6">
                <h2 className="text-base md:text-xl font-semibold text-foreground mb-3 leading-tight">
                  {currentQuestion.text}
                </h2>
                {currentQuestion.type === "multiple" && (
                  <p className="text-xs md:text-sm text-muted-foreground bg-secondary/30 p-2 rounded-md">
                    You can select multiple options. Click Next when ready to continue.
                  </p>
                )}
                {currentQuestion.type === "category-dropdown" && (
                  <p className="text-xs md:text-sm text-muted-foreground bg-secondary/30 p-2 rounded-md">
                    Click on the box you need help with and choose the symptoms you're having
                  </p>
                )}
                {currentQuestion.type === "emergency" && (
                  <p className="text-xs md:text-sm text-destructive font-medium bg-destructive/10 p-2 rounded-md border border-destructive/20">
                    Please select carefully. Emergency options will direct you to call 999 immediately.
                  </p>
                )}
                {currentQuestion.type !== "multiple" && currentQuestion.type !== "category-dropdown" && currentQuestion.type !== "emergency" && (
                  <p className="text-xs md:text-sm text-primary bg-primary/10 p-2 rounded-md">
                    Your answer will automatically advance to the next question
                  </p>
                )}
              </div>

              <QuestionRenderer
                question={currentQuestion}
                currentAnswer={currentAnswer}
                onAnswer={onAnswer}
              />

              {/* Navigation */}
              <div className="flex justify-between mt-4 md:mt-6 gap-2 md:gap-3">
                <Button
                  variant="outline"
                  onClick={onPrevious}
                  disabled={currentQuestionIndex === 0}
                  className="flex items-center min-h-[44px] px-3 md:px-4 text-sm"
                >
                  <ArrowLeft className="mr-1 md:mr-2 h-4 w-4" />
                  <span className="hidden sm:inline">Previous</span>
                  <span className="sm:hidden">Back</span>
                </Button>

                {(currentQuestion.type === "multiple" || currentQuestion.type === "category-dropdown") && (
                  <Button
                    onClick={onNext}
                    disabled={!isAnswered}
                    className="flex items-center min-h-[44px] px-3 md:px-4 text-sm"
                  >
                    <span className="hidden sm:inline">
                      {currentQuestionIndex === assessmentQuestions.length - 1 ? 'Get AI Analysis' : 'Next'}
                    </span>
                    <span className="sm:hidden">
                      {currentQuestionIndex === assessmentQuestions.length - 1 ? 'Analyze' : 'Next'}
                    </span>
                    <ArrowRight className="ml-1 md:ml-2 h-4 w-4" />
                  </Button>
                )}
              </div>
            </>
          )}
        </Card>
      </div>
    </main>
  );
};

export default AssessmentContent;